import { z } from 'zod/v4-mini';

import { Schema, cz } from '@/utils/zodUtils.ts';

export const glycemiaParametersTypeSchema = z.literal([
  'hypoglycemia',
  'hyperglycemia',
  'severeHypoglycemia',
  'severeHyperglycemia',
]);
export type GlycemiaParametersType = z.infer<
  typeof glycemiaParametersTypeSchema
>;
export type BaseGlycemiaParameters = {
  [K in GlycemiaParametersType]: number;
};

export const bgmGlycemiaParametersTypeSchema = z.literal([
  'fasting',
  'beforeMeal',
  'afterMeal',
]);
export type BGMGlycemiaParametersType = z.infer<
  typeof bgmGlycemiaParametersTypeSchema
>;

/**
 * ```
 *  {
 *    id: '123',
 *    hypoglycemia: 70,
 *    hyperglycemia: 180,
 *    severeHypoglycemia: 50,
 *    severeHyperglycemia: 300,
 *  }
 */
export type CGMGlycemiaParameters = {
  id: string;
} & BaseGlycemiaParameters;

/**
 * ```
 *  {
 *    id: '123',
 *    fasting: {
 *     severeHyperglycemia: 300,
 *      hyperglycemia: 180,
 *      hypoglycemia: 70,
 *      severeHypoglycemia: 50,
 *    },
 *    beforeMeal: {
 *      severeHyperglycemia: 300,
 *      hyperglycemia: 180,
 *      hypoglycemia: 0,
 *      severeHypoglycemia: 0,
 *    },
 *    afterMeal: {
 *     severeHyperglycemia: 300,
 *      hyperglycemia: 180,
 *      hypoglycemia: 70,
 *      severeHypoglycemia: 0,
 *    },
 *  }
 *  ```
 */
export type BGMGlycemiaParameters = {
  id: string;
} & {
  [K in BGMGlycemiaParametersType]: BaseGlycemiaParameters;
};

export type GlycemiaParameters =
  | {
      type: 'CGM';
      parameters: CGMGlycemiaParameters;
    }
  | {
      type: 'BGM';
      parameters: BGMGlycemiaParameters;
    };

export const glycemiaParametersSchema: Schema<BaseGlycemiaParameters> = z.pipe(
  z.object({
    hypoglycemia: cz.optional(z.number()),
    hyperglycemia: cz.optional(z.number()),
    severe_hypoglycemia: cz.optional(z.number()),
    severe_hyperglycemia: cz.optional(z.number()),
  }),
  z.transform(data => ({
    hypoglycemia: data.hypoglycemia ?? 0,
    hyperglycemia: data.hyperglycemia ?? 0,
    severeHypoglycemia: data.severe_hypoglycemia ?? 0,
    severeHyperglycemia: data.severe_hyperglycemia ?? 0,
  })),
);

export const cgmGlycemiaParametersSchema: Schema<CGMGlycemiaParameters> =
  z.intersection(glycemiaParametersSchema, z.object({ id: cz.id() }));

export const bgmGlycemiaParametersSchema: Schema<BGMGlycemiaParameters> =
  cz.camelCaseObject({
    id: cz.id(),
    fasting: glycemiaParametersSchema,
    before_meal: glycemiaParametersSchema,
    after_meal: glycemiaParametersSchema,
  });

export type EditGlycemiaParameters = {
  hypoglycemia?: number;
  hyperglycemia?: number;
  severe_hypoglycemia?: number;
  severe_hyperglycemia?: number;
};

export type EditCGMGlycemiaParameters = {
  id: string;
} & EditGlycemiaParameters;

export type EditBGMGlycemiaParameters = {
  id: string;
  fasting?: EditGlycemiaParameters;
  before_meal?: EditGlycemiaParameters;
  after_meal?: EditGlycemiaParameters;
};
